import { z } from 'zod';
import {
  RATE_MODE_OPTIONS,
  rateModeDefaults,
  rateModeFields,
  refineRateMode,
} from '../../../playground/endpoints/_shared';
import {
  COMPENSATION_PLAN_OPTIONS,
  GENDER_OPTIONS,
  PAYMENT_PERIODICITY_OPTIONS,
  contributionSchema,
} from '../_shared';

export {
  COMPENSATION_PLAN_OPTIONS,
  GENDER_OPTIONS,
  PAYMENT_PERIODICITY_OPTIONS,
  RATE_MODE_OPTIONS,
};

export const SAVINGS_MODE_OPTIONS = [
  { value: 'goal_value', label: 'Objetivo de ahorro' },
  { value: 'savings_capacity', label: 'Capacidad de ahorro' },
] as const;

export const beneficiarySchema = z.object({
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  duration_years: z.coerce.number().int(),
  annual_tuition: z.coerce.number(),
});

export type BeneficiaryFormValue = z.infer<typeof beneficiarySchema>;

// Types and required-ness only: capital bounds, coverage ratios, entry ages,
// premium floors and career/timeframe rules are the engine's to enforce and
// to report, so this page stays a thin client over the API. `timeframe` has
// no field at all -- it is never a client input on this product, always
// overwritten by `apply_derived_timeframe`.
const sharedFields = {
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  compensation_plan: z.enum(['A', 'B']),
  payment_periodicity: z.enum(['monthly', 'quarterly', 'semiannual', 'annual']),
  ...rateModeFields,
  beneficiaries: z.array(beneficiarySchema),
  contributions: z.array(contributionSchema),
  death_insured_capital: z.coerce.number(),
  accidental_death_insured_capital: z.coerce.number(),
  itp_insured_capital: z.coerce.number(),
  serious_illness_insured_capital: z.coerce.number(),
};

// Which fields each mode carries -- the client-side mirror of
// `validators/registry.MODE_RULES`, used to drive field visibility.
export const universitySavingsV2Schema = z
  .discriminatedUnion('savings_mode', [
    z.object({
      ...sharedFields,
      savings_mode: z.literal('goal_value'),
    }),
    z.object({
      ...sharedFields,
      savings_mode: z.literal('savings_capacity'),
      agreed_premium: z.coerce.number(),
    }),
  ])
  // The only cross-field rule kept: `rate_mode` is a UI toggle deciding which
  // of two mutually exclusive fields the payload carries, so the form has to
  // resolve it before a request can be built at all.
  .superRefine(refineRateMode);

export type UniversitySavingsV2FormValues = z.infer<typeof universitySavingsV2Schema>;

const sharedDefaults = {
  date_of_birth: '1990-01-01' as const,
  gender: 'MALE' as const,
  compensation_plan: 'A' as const,
  payment_periodicity: 'monthly' as const,
  ...rateModeDefaults,
  projection_rate: 0.02,
  beneficiaries: [
    { date_of_birth: '2015-06-01', duration_years: 5, annual_tuition: 120 },
  ],
  contributions: [],
  // Seeds the savings_capacity-only field so it survives mode switches via
  // AutoForm's `keepDefaultValue` unregister.
  agreed_premium: 5,
  death_insured_capital: 1000,
  accidental_death_insured_capital: 0,
  itp_insured_capital: 0,
  serious_illness_insured_capital: 0,
};

export const universitySavingsV2Defaults: UniversitySavingsV2FormValues = {
  ...sharedDefaults,
  savings_mode: 'goal_value',
};
